import styled, {css} from 'styled-components'

export const StyledCounterCard = styled.div`
    ${({theme}) => css`
        border: 1px solid ${theme.palette.primary['100']};
        border-radius: 8px;
        background: ${theme.palette.primary['50']};
        overflow: hidden;
    `}
`

export const StyledCounterBand = styled.div`
    ${({theme}) => css`
        display: flex;
        align-items: center;
        justify-content: space-between;
        flex-wrap: wrap;
        gap: ${theme.spacing * 2}px;
        padding: ${theme.spacing * 3}px;
    `}
`

export const StyledCounterInfo = styled.div`
    ${({theme}) => css`
        display: flex;
        align-items: center;
        gap: ${theme.spacing * 2}px;
    `}
`

export const StyledCounterIconCircle = styled.div`
    ${({theme}) => css`
        width: 32px;
        height: 32px;
        border-radius: 50%;
        background: ${theme.palette.neutral.white};
        border: 1px solid ${theme.palette.primary['200']};
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;

        & svg {
            width: 16px;
            height: 16px;
            color: ${theme.palette.primary['600']};
        }
    `}
`

export const StyledCounterTexts = styled.div`
    ${({theme}) => css`
        display: flex;
        flex-direction: column;

        & > p {
            ${theme.typography.textSm};
            color: ${theme.palette.neutral['600']};

            & > strong {
                font-weight: 500;
                font-variant-numeric: tabular-nums;
                color: ${theme.palette.neutral['900']};
            }
        }

        & > small {
            ${theme.typography.textXs};
            color: ${theme.palette.neutral['500']};
        }
    `}
`

export const StyledPreviewToggle = styled.button`
    ${({theme}) => css`
        display: inline-flex;
        align-items: center;
        gap: ${theme.spacing}px;
        padding: ${theme.spacing * 1.5}px ${theme.spacing * 3}px;
        border: none;
        border-radius: 6px;
        background: transparent;
        ${theme.typography.textXs};
        font-weight: 500;
        color: ${theme.palette.primary['700']};
        cursor: pointer;
        transition: ${theme.transition};

        &:hover {
            background: ${theme.palette.neutral.white};
        }

        & svg {
            width: 14px;
            height: 14px;
        }
    `}
`

export const StyledPreviewSection = styled.div`
    ${({theme}) => css`
        background: ${theme.palette.neutral.white};
        border-top: 1px solid ${theme.palette.primary['100']};
        padding: ${theme.spacing * 3}px ${theme.spacing * 4}px;
    `}
`

export const StyledPreviewHeader = styled.div`
    ${({theme}) => css`
        display: flex;
        align-items: baseline;
        justify-content: space-between;
        gap: ${theme.spacing * 2}px;
        padding-bottom: ${theme.spacing * 2}px;

        & > strong {
            ${theme.typography.textXs};
            font-weight: 600;
            color: ${theme.palette.neutral['700']};
        }

        & > span {
            ${theme.typography.textXs};
            font-variant-numeric: tabular-nums;
            color: ${theme.palette.neutral['400']};
        }
    `}
`

export const StyledPreviewTabs = styled.div`
    ${({theme}) => css`
        display: flex;
        gap: ${theme.spacing}px;
        border-bottom: 1px solid ${theme.palette.neutral['100']};
        margin-bottom: ${theme.spacing * 2}px;
    `}
`

export const StyledPreviewTab = styled.button<{$active: boolean}>`
    ${({theme, $active}) => css`
        display: inline-flex;
        align-items: center;
        gap: ${theme.spacing * 1.5}px;
        padding: ${theme.spacing * 1.5}px ${theme.spacing * 2}px;
        margin-bottom: -1px;
        ${theme.typography.textXs};
        font-weight: 500;
        background: transparent;
        border: none;
        border-bottom: 2px solid ${$active ? theme.palette.primary['600'] : 'transparent'};
        color: ${$active ? theme.palette.primary['600'] : theme.palette.neutral['500']};
        cursor: pointer;
        transition: ${theme.transition};

        &:hover {
            color: ${$active ? theme.palette.primary['600'] : theme.palette.neutral['700']};
        }
    `}
`

export const StyledPreviewTabCounter = styled.span<{$active: boolean}>`
    ${({theme, $active}) => css`
        padding: ${theme.spacing * 0.5}px ${theme.spacing * 1.5}px;
        border-radius: 999px;
        font-size: 10px;
        line-height: 14px;
        background: ${$active ? theme.palette.primary['100'] : theme.palette.neutral['100']};
        color: ${$active ? theme.palette.primary['700'] : theme.palette.neutral['500']};
    `}
`

export const StyledPreviewScrollArea = styled.div`
    max-height: 320px;
    overflow-y: auto;
`

export const StyledPreviewList = styled.ul<{$isRefreshing?: boolean}>`
    ${({theme, $isRefreshing}) => css`
        list-style: none;
        margin: 0;
        padding: 0;
        transition: ${theme.transition};
        opacity: ${$isRefreshing ? 0.55 : 1};
    `}
`

export const StyledPreviewRow = styled.li`
    ${({theme}) => css`
        display: grid;
        grid-template-columns: auto minmax(0, 1fr) auto;
        align-items: center;
        gap: ${theme.spacing * 3}px;
        padding: ${theme.spacing * 2}px 0;

        & + & {
            border-top: 1px solid ${theme.palette.neutral['100']};
        }
    `}
`

export const StyledPreviewIdentity = styled.div`
    ${({theme}) => css`
        display: flex;
        flex-direction: column;
        min-width: 0;

        & > strong {
            ${theme.typography.textSm};
            font-weight: 500;
            color: ${theme.palette.neutral['900']};
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;

            &.missing {
                font-weight: 400;
                font-style: italic;
                color: ${theme.palette.neutral['400']};
            }
        }

        & > span {
            ${theme.typography.textXs};
            color: ${theme.palette.neutral['500']};
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }
    `}
`

export const StyledPreviewMore = styled.p`
    ${({theme}) => css`
        ${theme.typography.textXs};
        font-variant-numeric: tabular-nums;
        color: ${theme.palette.neutral['400']};
        border-top: 1px solid ${theme.palette.neutral['100']};
        padding-top: ${theme.spacing * 2}px;
    `}
`

export const StyledPreviewEmpty = styled.p`
    ${({theme}) => css`
        ${theme.typography.textXs};
        color: ${theme.palette.neutral['500']};
        padding: ${theme.spacing * 2}px 0;
    `}
`

export const StyledSkeletonRow = styled.div`
    ${({theme}) => css`
        display: grid;
        grid-template-columns: auto minmax(0, 1fr) auto;
        align-items: center;
        gap: ${theme.spacing * 3}px;
        padding: ${theme.spacing * 2}px 0;

        & + & {
            border-top: 1px solid ${theme.palette.neutral['100']};
        }
    `}
`

export const StyledKindBadge = styled.span<{$kind: 'patient' | 'consultant'}>`
    ${({theme, $kind}) => css`
        padding: ${theme.spacing * 0.5}px ${theme.spacing * 1.5}px;
        border-radius: 4px;
        font-size: 10px;
        line-height: 14px;
        font-weight: 500;
        flex-shrink: 0;
        background: ${$kind === 'patient' ? theme.palette.primary['100'] : theme.palette.purple['100']};
        color: ${$kind === 'patient' ? theme.palette.primary['700'] : theme.palette.purple['700']};
    `}
`
